import { classNames, Icon, iconProps, useUpdateRef } from '@zambezi/sdk/common'
import PropTypes from 'prop-types'
import React, { forwardRef, useCallback, useRef } from 'react'
import Lookup from './Lookup'
import SelectInput from './SelectInput'
import SelectMobile from './SelectMobile'

/**
 * Create a React Select component
 *
 * @template T, Item
 * @param {import('./interfaces').SelectProps<T, Item>} props
 * @param {React.Ref} ref
 * @returns {JSX.Element}
 */
function select (props, ref) {
  const {
    className,
    isMobile,
    withGrouping,
    renderInput,
    getGroupChildren,
    disableHighlightByKey,
    renderSuggestion,
    getSuggestionValue,
    isDisabled,
    suggestions,
    disabledItemsIndices,
    highlightBy,
    iconProps,
    selectedIconProps,
    openOnFocus,
    showErrorMessage,
    error: propError = '',
    isError,
    withLabel,
    label
  } = props
  const elementRef = useRef()
  const shouldRenderNoResultsMessage = useCallback(() => false, [])

  const suggestionClassName = useCallback(
    (suggestion, { query }) => {
      const isSelected = getSuggestionValue(suggestion) === query
      return classNames({
        'zb-select-dropdown-list-item-is-selected': isSelected
      })
    },
    [getSuggestionValue]
  )

  const suggestionProps = useCallback(
    (suggestion, { query }) => {
      const isSelected = getSuggestionValue(suggestion) === query
      return {
        'aria-selected': isSelected
      }
    },
    [getSuggestionValue]
  )

  const shouldRenderSuggestions = useCallback(
    (value, event) => {
      const hasSuggestions = !!suggestions.length
      if (event.type === 'focus') {
        return openOnFocus && hasSuggestions
      }
      return hasSuggestions
    },
    [suggestions, openOnFocus]
  )

  const renderSelectInput = useCallback(
    (props) => (
      <SelectInput
        {...{
          ...props,
          disabledItemsIndices,
          disableHighlightByKey,
          elementRef,
          getGroupChildren,
          getSuggestionValue,
          highlightBy,
          iconProps,
          isDisabled,
          renderInput,
          suggestions,
          withGrouping
        }}
      />
    ),
    [
      renderInput,
      isDisabled,
      disableHighlightByKey,
      highlightBy,
      suggestions,
      withGrouping,
      getGroupChildren,
      getSuggestionValue
    ]
  )

  const renderSelectSuggestion = useCallback(
    (suggestion, { query, getSuggestionValue }) => {
      const isSelected = query === getSuggestionValue(suggestion)

      return (
        <>
          {renderSuggestion(suggestion, { query, getSuggestionValue }, isSelected)}

          {isSelected && (
            <Icon
              name='core-dropdown-item-selected'
              aria-hidden='true'
              title=''
              iconProps={selectedIconProps}
            />
          )}
        </>
      )
    },
    [renderSuggestion, selectedIconProps]
  )

  useUpdateRef(ref, elementRef)
  const Component = isMobile ? SelectMobile : Lookup

  return (
    <Component
      {...props}
      ref={elementRef}
      suggestions={suggestions}
      className={classNames('zb-select', className)}
      renderInput={renderSelectInput}
      renderSuggestion={renderSelectSuggestion}
      suggestionClassName={suggestionClassName}
      suggestionProps={suggestionProps}
      shouldRenderSuggestions={shouldRenderSuggestions}
      shouldRenderNoResultsMessage={shouldRenderNoResultsMessage}
      showErrorMessage={showErrorMessage}
      error={propError}
      isError={isError}
      withLabel={withLabel}
      label={label}
    />
  )
}

function noop () {}

/**
 * @template T
 * @template Item
 * @type {React.ForwardRefExoticComponent<import('./interfaces').SelectProps<T, Item> & React.RefAttributes<import('./interfaces').LookupRef>>}
 */
const Select = forwardRef(select)

Select.displayName = 'Select'

Select.propTypes = {
  isMobile: PropTypes.bool,
  suggestions: PropTypes.array,
  disabledItemsIndices: PropTypes.array,
  getSuggestionValue: PropTypes.func,
  defaultValue: PropTypes.string,
  value: PropTypes.string,
  renderInput: PropTypes.func,
  renderSuggestion: PropTypes.func,
  onSuggestionSelected: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  openOnFocus: PropTypes.bool,
  disableHighlightByKey: PropTypes.bool,
  highlightBy: PropTypes.func,
  selectedIconProps: iconProps,
  iconProps,
  showErrorMessage: PropTypes.bool,
  isError: PropTypes.bool,
  error: PropTypes.any,
  withLabel: PropTypes.bool,
  label: PropTypes.string
}

Select.defaultProps = {
  isMobile: false,
  suggestions: [],
  disabledItemsIndices: [],
  getSuggestionValue: String,
  defaultValue: '',
  value: undefined,
  renderInput: defaultRenderInput,
  renderSuggestion: defaultRenderSuggestion,
  onSuggestionSelected: noop,
  onChange: noop,
  onBlur: noop,
  onFocus: noop,
  openOnFocus: true,
  disableHighlightByKey: false,
  highlightBy: (d) => d
}

export default Select

/**
 * @template T
 * @template Item
 * @param {import('./interfaces').SelectInputProps<T, Item>} param0
 */
function defaultRenderInput ({
  isFocused,
  isError,
  isDisabled,
  isOpen,
  focusedSuggestionValue,
  getSuggestionValue,
  suggestions,
  readOnly,
  ...props
}) {
  const inputProps = {
    ...props,
    type: props.type || 'text',
    autoComplete: 'off',
    readOnly: true,
    placeholder: props.value ? null : props.placeholder
  }

  return (
    <input {...inputProps} />
  )
}

function defaultRenderSuggestion (suggestion, { getSuggestionValue }, isSelected) {
  const label = getSuggestionValue(suggestion)

  return (
    <>
      <span>{label.length ? label : '\u00a0'}</span>
      {isSelected && <span className='zb-slider-description-sr-only'>Selected </span>}
    </>
  )
}
